import React, { useState, useEffect, useRef } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faDownload, faUpload } from "@fortawesome/free-solid-svg-icons"
import { ExcelUpload } from "../types/CampaignDatas"
import {
  TAgetExcelUploads,
  TAdownloadOriginalExcel,
  TAuploadExcelFile,
  TAupdateExcelUploadStatus,
} from "../services/campaignsAPI"
import { useNavigate } from "react-router-dom"
import { useSelector } from "react-redux"
import { selectUser } from "../redux/store/auth/authSlice"
import { getErrorStatus, getErrorMessage } from "../types/error"

interface ExcelUploadsModalProps {
  isOpen: boolean
  onClose: () => void
  campaignId: string
  onUploadComplete?: () => void
}

const ExcelUploadsModal: React.FC<ExcelUploadsModalProps> = ({
  isOpen,
  onClose,
  campaignId,
  onUploadComplete,
}) => {
  const navigate = useNavigate()
  const userEmail = useSelector(selectUser)?.replace(/"/g, "") || ""
  const [uploads, setUploads] = useState<ExcelUpload[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState<boolean>(false)
  const [uploadError, setUploadError] = useState<string>("")
  const uploadsRef = useRef<ExcelUpload[]>([])

  useEffect(() => {
    if (isOpen) {
      loadUploads()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, campaignId])

  const loadUploads = async () => {
    try {
      setLoading(true)
      const data = await TAgetExcelUploads(campaignId)
      const uploadsData = data.uploads || []
      setUploads(uploadsData)
      uploadsRef.current = uploadsData
    } catch (error: unknown) {
      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
    } finally {
      setLoading(false)
    }
  }

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) {
      return
    }

    // Check if file is Excel
    const fileExtension = file.name.split(".").pop()?.toLowerCase()
    if (fileExtension !== "xlsx" && fileExtension !== "xls") {
      setUploadError("Lütfen sadece Excel dosyası (.xlsx veya .xls) seçin")
      return
    }

    setSelectedFile(file)
    setUploadError("")
  }

  const handleUploadFile = async () => {
    if (!selectedFile) {
      setUploadError("Lütfen bir dosya seçin")
      return
    }

    setUploading(true)
    setUploadError("")

    try {
      const uploadResponse = await TAuploadExcelFile(campaignId, selectedFile, userEmail)
      setSelectedFile(null)
      const uploadId = uploadResponse.uploadId || uploadResponse._id
      await loadUploads()
      if (uploadId) {
        try {
          await TAupdateExcelUploadStatus(
            campaignId,
            uploadId,
            "completed",
            0, // successCount - not applicable for simple uploads
            0, // failureCount - not applicable for simple uploads
            [], // errorMessages - not applicable for simple uploads
          )
        } catch (updateError) {
          // eslint-disable-next-line no-console
          console.error("Upload durumu güncelleme hatası:", updateError)
        }
      }

      if (onUploadComplete) {
        onUploadComplete()
      }
    } catch (error: unknown) {
      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      setUploadError(`Dosya yükleme hatası: ${getErrorMessage(error) || error}`)
    } finally {
      setUploading(false)
    }
  }

  const handleDownloadOriginal = async (uploadId: string, filename: string) => {
    try {
      const blob = await TAdownloadOriginalExcel(campaignId, uploadId)
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (error: unknown) {
      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "text-green-600"
      case "processing":
        return "text-yellow-600"
      case "failed":
        return "text-red-600"
      default:
        return "text-gray-600"
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "completed":
        return "Tamamlandı"
      case "processing":
        return "İşleniyor"
      case "failed":
        return "Başarısız"
      case "pending":
        return "Bekliyor"
      default:
        return status
    }
  }

  if (!isOpen) {
    return null
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">Kampanya Excel Yüklemeleri</h2>
            <div className="flex items-center space-x-4">
              {/* Upload Section */}
              <div className="flex items-center space-x-2">
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFile}
                  className="hidden"
                  id="excel-upload-input"
                />
                <label
                  htmlFor="excel-upload-input"
                  className="bg-white text-blue-600 px-3 py-2 rounded-md text-sm font-medium hover:bg-gray-100 cursor-pointer flex items-center space-x-2"
                >
                  <FontAwesomeIcon icon={faUpload} />
                  <span>Dosya Seç</span>
                </label>
                {selectedFile && (
                  <button
                    onClick={handleUploadFile}
                    disabled={uploading}
                    className="bg-green-500 text-white px-3 py-2 rounded-md text-sm font-medium hover:bg-green-600 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    <FontAwesomeIcon icon={faUpload} />
                    <span>{uploading ? "Yükleniyor..." : "Yükle"}</span>
                  </button>
                )}
              </div>
              <button onClick={onClose} className="text-white hover:text-gray-200 text-2xl">
                ×
              </button>
            </div>
          </div>

          {/* Selected file info */}
          {selectedFile && (
            <div className="mt-3 p-2 bg-white bg-opacity-20 rounded-md">
              <p className="text-sm">
                Seçilen dosya: <strong>{selectedFile.name}</strong>(
                {(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
              </p>
            </div>
          )}

          {/* Upload error */}
          {uploadError && (
            <div className="mt-3 p-2 bg-red-500 bg-opacity-20 rounded-md">
              <p className="text-sm text-red-100">{uploadError}</p>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
            </div>
          ) : uploads.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              <p>Bu kampanya için henüz Excel yüklemesi bulunmuyor.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {uploads.map((upload) => (
                <div key={upload._id} className="border rounded-lg p-4 hover:bg-gray-50">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <h3 className="font-semibold text-lg">{upload.original_filename}</h3>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                            upload.processing_status,
                          )}`}
                        >
                          {getStatusLabel(upload.processing_status)}
                        </span>
                      </div>

                      <div className="mt-2 text-sm text-gray-600">
                        <p>
                          Yüklenme Tarihi: {new Date(upload.uploaded_at).toLocaleString("tr-TR")}
                        </p>
                        <p>Yükleyen: {upload.uploaded_by}</p>
                        <p>Toplam Satır: {upload.total_rows}</p>
                        {upload.processing_status === "completed" && (
                          <>
                            <p>Başarılı: {upload.success_rows}</p>
                            <p>Başarısız: {upload.failed_rows}</p>
                          </>
                        )}
                      </div>

                      {upload.error_messages && upload.error_messages.length > 0 && (
                        <div className="mt-2">
                          <p className="text-sm font-medium text-red-600">Hatalar:</p>
                          <ul className="text-xs text-red-500 mt-1">
                            {upload.error_messages.slice(0, 3).map((error, index) => (
                              <li key={index}>• {error}</li>
                            ))}
                            {upload.error_messages.length > 3 && (
                              <li>• ... ve {upload.error_messages.length - 3} hata daha</li>
                            )}
                          </ul>
                        </div>
                      )}
                    </div>

                    <div className="flex space-x-2 ml-4">
                      <button
                        onClick={() => handleDownloadOriginal(upload._id, upload.original_filename)}
                        className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                      >
                        <FontAwesomeIcon icon={faDownload} className="mr-2" />
                        Dosyayı İndir
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t p-4 bg-gray-50">
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
            >
              Kapat
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ExcelUploadsModal
